import { type ChangeEvent, type DragEvent, useCallback, useState } from 'react';

import { CloseIcon, ImageIcon } from '@/shared/ui/icons';

type MultipleDropZoneProps = {
  images: string[];
  maxImages?: number;
  onImagesUpload: (files: File[]) => void;
  onImageRemove: (index: number) => void;
  isLoading?: boolean;
  accept?: string;
  uploadedFileNames?: string[]; // Track uploaded file names to prevent duplicates
  existingImagesCount?: number; // Count of already uploaded images on server
  width?: string; // Custom width for drop zone
};

export const MultipleDropZone = ({
  images,
  maxImages = 100,
  onImagesUpload,
  onImageRemove,
  isLoading = false,
  accept = 'image/jpg,image/jpeg,image/png,image/webp',
  uploadedFileNames = [],
  existingImagesCount = 0,
  width,
}: MultipleDropZoneProps) => {
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState<string>('');

  const validateFiles = useCallback(
    (files: File[]): { valid: boolean; error?: string } => {
      // Total images = existing on server + current preview images
      const totalCurrentImages = existingImagesCount + images.length;
      const remainingSlots = maxImages - totalCurrentImages;

      if (remainingSlots === 0) {
        return {
          valid: false,
          error: `Maximum ${maxImages} images allowed (${existingImagesCount} already uploaded)`,
        };
      }

      if (files.length > remainingSlots) {
        return {
          valid: false,
          error: `Can only add ${remainingSlots} more image(s) (${existingImagesCount} already uploaded)`,
        };
      }

      // Validate each file
      for (const file of files) {
        // Check file type
        if (!file.type.startsWith('image/')) {
          return { valid: false, error: 'Only image files are allowed' };
        }

        // Check file size (max 10MB)
        if (file.size > 10 * 1024 * 1024) {
          return { valid: false, error: `${file.name} is too large. Max size is 10MB` };
        }

        // Check for duplicate file names
        if (uploadedFileNames.includes(file.name)) {
          return { valid: false, error: `File "${file.name}" is already uploaded` };
        }
      }

      return { valid: true };
    },
    [maxImages, images.length, uploadedFileNames, existingImagesCount],
  );

  const handleDragEnter = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
    setError('');
  };

  const handleDragLeave = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDrop = useCallback(
    (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);

      const files = Array.from(e.dataTransfer.files);
      const validation = validateFiles(files);

      if (validation.valid) {
        onImagesUpload(files);
        setError('');
      } else if (validation.error) {
        setError(validation.error);
      }
    },
    [onImagesUpload, validateFiles],
  );

  const handleFileSelect = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.target.files || []);
      const validation = validateFiles(files);

      if (validation.valid) {
        onImagesUpload(files);
        setError('');
      } else if (validation.error) {
        setError(validation.error);
      }

      e.target.value = ''; // Reset input
    },
    [onImagesUpload, validateFiles],
  );

  const hasImages = images.length > 0;
  const totalCurrentImages = existingImagesCount + images.length;
  const canAddMore = totalCurrentImages < maxImages;

  return (
    <div className="space-y-4" style={width ? { width } : undefined}>
      {/* Upload area - smaller on mobile */}
      {canAddMore && (
        <div
          className={`
            relative p-3 sm:p-4 border-2 border-dashed rounded-lg transition-all duration-200 cursor-pointer
            ${
              error
                ? 'border-red-500 bg-red-500/10'
                : isDragging
                  ? 'border-violet-500 bg-violet-500/10'
                  : 'border-zinc-600 hover:border-zinc-500 bg-zinc-800/50'
            }
          `}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
        >
          <input
            type="file"
            id="persona-images-upload"
            className="hidden"
            accept={accept}
            multiple
            onChange={handleFileSelect}
            disabled={isLoading}
          />
          <label
            htmlFor="persona-images-upload"
            className="cursor-pointer flex flex-col items-center justify-center py-2 sm:py-3"
          >
            <ImageIcon
              className={`w-8 h-8 sm:w-10 sm:h-10 mb-2 ${isDragging ? 'text-violet-400' : 'text-zinc-400'}`}
            />
            <p className="text-xs sm:text-sm font-medium text-white mb-1 text-center">
              {isDragging ? 'Drop images here' : 'Click to upload or drag & drop'}
            </p>
            <p className="text-xs text-zinc-400">
              JPG, JPEG, PNG, WEBP • Max 10MB • {totalCurrentImages}/{maxImages}
              {existingImagesCount > 0 && ` (${existingImagesCount} uploaded)`}
            </p>
          </label>
        </div>
      )}

      {/* Error message */}
      {error && <p className="text-sm text-red-400">{error}</p>}

      {/* Image grid - responsive columns */}
      {hasImages && (
        <div className="grid grid-cols-5 sm:grid-cols-6 md:grid-cols-8 gap-2">
          {images.map((image, index) => (
            <div key={index} className="relative group aspect-square">
              <img
                src={image}
                alt={`Upload ${index + 1}`}
                className="w-full h-full object-cover rounded-md border border-zinc-700"
              />
              <button
                onClick={() => onImageRemove(index)}
                className="absolute -top-1 -right-1 p-0.5 bg-red-500 rounded-full opacity-0 group-hover:opacity-100 transition-opacity 
                          md:opacity-0 md:group-hover:opacity-100 
                          touch:opacity-100 cursor-pointer"
                disabled={isLoading}
                aria-label="Remove image"
              >
                <CloseIcon className="w-3 h-3 text-white" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Info text */}
      {!hasImages && !error && (
        <p className="text-xs text-zinc-500 text-center">
          {existingImagesCount > 0
            ? `${existingImagesCount} photos already uploaded. Add ${maxImages - existingImagesCount} more for best results`
            : 'Upload 1-20 photos for best results'}
        </p>
      )}
    </div>
  );
};
